import React, { useState, useMemo } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { 
    ArrowLeft, 
    Home, 
    TrendingUp, 
    Calendar, 
    Download, 
    Filter,
    Users,
    Clock,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Loader2,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';

interface OccupancyData {
    propertyId: string;
    propertyName: string;
    totalUnits: number;
    occupiedUnits: number;
    availableUnits: number;
    occupancyRate: number;
    avgOccupancyDuration: number;
    vacancyRate: number;
    turnoverRate: number;
    avgDaysVacant: number;
    projectedRevenue: number;
    actualRevenue: number;
    revenueLoss: number;
    monthlyTrend: {
        month: string;
        occupancyRate: number;
        newTenants: number;
        moveOuts: number;
    }[];
    unitStatus: {
        unitNumber: string;
        status: 'Occupied' | 'Available' | 'Maintenance';
        daysVacant?: number;
        tenantName?: string;
        leaseEnd?: string;
    }[];
}

export const OccupancyAnalytics: React.FC = () => {
    const navigate = useNavigate();
    const { success } = useToast();
    const { properties, units, loading } = useLandlordStore();
    const [selectedProperty, setSelectedProperty] = useState<string>('all');
    const [dateRange, setDateRange] = useState<string>('6months');
    const [activeView, setActiveView] = useState<'overview' | 'trends' | 'units'>('overview');

    // Mock occupancy data
    const mockOccupancyData: OccupancyData[] = [
        {
            propertyId: 'prop_1',
            propertyName: 'Sunrise Apartments',
            totalUnits: 24,
            occupiedUnits: 22,
            availableUnits: 2,
            occupancyRate: 91.7,
            avgOccupancyDuration: 18,
            vacancyRate: 8.3,
            turnoverRate: 6.5,
            avgDaysVacant: 12,
            projectedRevenue: 1296000,
            actualRevenue: 1188000,
            revenueLoss: 108000,
            monthlyTrend: [
                { month: 'Jan', occupancyRate: 87.5, newTenants: 2, moveOuts: 1 },
                { month: 'Feb', occupancyRate: 91.7, newTenants: 1, moveOuts: 0 },
                { month: 'Mar', occupancyRate: 95.8, newTenants: 1, moveOuts: 0 },
                { month: 'Apr', occupancyRate: 91.7, newTenants: 0, moveOuts: 1 },
                { month: 'May', occupancyRate: 95.8, newTenants: 1, moveOuts: 0 },
                { month: 'Jun', occupancyRate: 91.7, newTenants: 0, moveOuts: 1 }
            ],
            unitStatus: [
                { unitNumber: 'A101', status: 'Occupied', tenantName: 'John Kamau', leaseEnd: '2026-12-31' },
                { unitNumber: 'A102', status: 'Occupied', tenantName: 'Sarah Mwangi', leaseEnd: '2026-08-15' },
                { unitNumber: 'A103', status: 'Available', daysVacant: 14 },
                { unitNumber: 'A104', status: 'Occupied', tenantName: 'Peter Ochieng', leaseEnd: '2027-03-01' },
                { unitNumber: 'B201', status: 'Occupied', tenantName: 'Mary Wanjiku', leaseEnd: '2026-09-30' },
                { unitNumber: 'B202', status: 'Maintenance', daysVacant: 7 }
            ]
        },
        {
            propertyId: 'prop_2',
            propertyName: 'Majengo Heights',
            totalUnits: 36,
            occupiedUnits: 30,
            availableUnits: 6,
            occupancyRate: 83.3,
            avgOccupancyDuration: 14,
            vacancyRate: 16.7,
            turnoverRate: 10.2,
            avgDaysVacant: 21,
            projectedRevenue: 1641600,
            actualRevenue: 1368000,
            revenueLoss: 273600,
            monthlyTrend: [
                { month: 'Jan', occupancyRate: 80.6, newTenants: 3, moveOuts: 2 },
                { month: 'Feb', occupancyRate: 83.3, newTenants: 1, moveOuts: 0 },
                { month: 'Mar', occupancyRate: 86.1, newTenants: 2, moveOuts: 1 },
                { month: 'Apr', occupancyRate: 80.6, newTenants: 0, moveOuts: 2 },
                { month: 'May', occupancyRate: 83.3, newTenants: 2, moveOuts: 1 },
                { month: 'Jun', occupancyRate: 83.3, newTenants: 1, moveOuts: 1 }
            ],
            unitStatus: [
                { unitNumber: 'A101', status: 'Occupied', tenantName: 'Alice Brown', leaseEnd: '2026-11-15' },
                { unitNumber: 'A102', status: 'Available', daysVacant: 28 },
                { unitNumber: 'A103', status: 'Occupied', tenantName: 'Bob White', leaseEnd: '2027-01-31' },
                { unitNumber: 'A104', status: 'Available', daysVacant: 35 },
                { unitNumber: 'B201', status: 'Occupied', tenantName: 'Charlie Black', leaseEnd: '2026-07-31' },
                { unitNumber: 'B202', status: 'Available', daysVacant: 14 }
            ]
        }
    ];

    const filteredData = useMemo(() => {
        if (selectedProperty === 'all') {
            return mockOccupancyData;
        }
        return mockOccupancyData.filter(p => p.propertyId === selectedProperty);
    }, [selectedProperty]);

    const aggregateData = useMemo(() => {
        if (filteredData.length === 0) return null;
        
        const totalUnits = filteredData.reduce((sum, p) => sum + p.totalUnits, 0);
        const totalOccupied = filteredData.reduce((sum, p) => sum + p.occupiedUnits, 0);
        const totalAvailable = filteredData.reduce((sum, p) => sum + p.availableUnits, 0);
        const avgOccupancyRate = filteredData.reduce((sum, p) => sum + p.occupancyRate, 0) / filteredData.length;
        const totalRevenueLoss = filteredData.reduce((sum, p) => sum + p.revenueLoss, 0);
        
        return { totalUnits, totalOccupied, totalAvailable, avgOccupancyRate, totalRevenueLoss };
    }, [filteredData]);

    const handleDownloadReport = () => {
        success('Download Started', 'Occupancy analytics report is being generated...');
    };

    const formatCurrency = (value: number) => {
        return `KES ${value.toLocaleString()}`;
    };

    const getOccupancyStatus = (rate: number) => {
        if (rate >= 90) return { label: 'Excellent', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30' };
        if (rate >= 75) return { label: 'Good', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' };
        if (rate >= 60) return { label: 'Fair', color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/30' };
        return { label: 'Poor', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' };
    };

    const getUnitStatusStyle = (status: string) => {
        switch (status) {
            case 'Occupied':
                return { text: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30', icon: CheckCircle };
            case 'Available':
                return { text: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30', icon: Home };
            case 'Maintenance':
                return { text: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/30', icon: AlertTriangle };
            default:
                return { text: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-900/30', icon: XCircle };
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="animate-spin text-primary w-10 h-10" />
                <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading occupancy data...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12 min-h-[80vh]">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 border-b border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <div className="flex-1">
                    <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Occupancy Analytics</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Track and analyze occupancy rates across your properties</p>
                </div>
                <button
                    onClick={handleDownloadReport}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    <Download size={18} />
                    Export Report
                </button>
            </div>

            {/* Filters */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border px-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="md:w-64">
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                            Select Property
                        </label>
                        <select
                            value={selectedProperty}
                            onChange={(e) => setSelectedProperty(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text transition-all"
                        >
                            <option value="all">All Properties</option>
                            {properties.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="md:w-48">
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                            Date Range
                        </label>
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text transition-all"
                        >
                            <option value="1month">Last Month</option>
                            <option value="3months">Last 3 Months</option>
                            <option value="6months">Last 6 Months</option>
                            <option value="1year">Last Year</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            {aggregateData && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 px-4">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Total Units</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">{aggregateData.totalUnits}</h3>
                            </div>
                            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <Home size={24} className="text-blue-600 dark:text-blue-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Occupied</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">{aggregateData.totalOccupied}</h3>
                                <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">{aggregateData.totalUnits - aggregateData.totalOccupied} available</p>
                            </div>
                            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                <CheckCircle size={24} className="text-green-600 dark:text-green-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Occupancy Rate</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">{aggregateData.avgOccupancyRate.toFixed(1)}%</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${getOccupancyStatus(aggregateData.avgOccupancyRate).bg} ${getOccupancyStatus(aggregateData.avgOccupancyRate).color}`}>
                                    {getOccupancyStatus(aggregateData.avgOccupancyRate).label}
                                </span>
                            </div>
                            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                <TrendingUp size={24} className="text-purple-600 dark:text-purple-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Avg Days Vacant</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">
                                    {(filteredData.reduce((sum, p) => sum + p.avgDaysVacant, 0) / filteredData.length).toFixed(0)}
                                </h3>
                            </div>
                            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                <Clock size={24} className="text-orange-600 dark:text-orange-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Revenue Loss</p>
                                <h3 className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">{formatCurrency(aggregateData.totalRevenueLoss)}</h3>
                                <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">From vacancies</p>
                            </div>
                            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                <AlertTriangle size={24} className="text-red-600 dark:text-red-400" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Tabs */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border px-4">
                <div className="flex border-b border-light-border dark:border-dark-border">
                    <button
                        onClick={() => setActiveView('overview')}
                        className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                            activeView === 'overview'
                                ? 'text-primary border-primary'
                                : 'text-light-subtle dark:text-dark-subtle border-transparent hover:text-light-text dark:hover:text-dark-text'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveView('trends')}
                        className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                            activeView === 'trends'
                                ? 'text-primary border-primary'
                                : 'text-light-subtle dark:text-dark-subtle border-transparent hover:text-light-text dark:hover:text-dark-text'
                        }`}
                    >
                        Trends
                    </button>
                    <button
                        onClick={() => setActiveView('units')}
                        className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                            activeView === 'units'
                                ? 'text-primary border-primary'
                                : 'text-light-subtle dark:text-dark-subtle border-transparent hover:text-light-text dark:hover:text-dark-text'
                        }`}
                    >
                        Unit Status
                    </button>
                </div>

                <div className="p-6">
                    {activeView === 'overview' && (
                        <div className="space-y-6">
                            {/* Property Overview Table */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-light-border dark:border-dark-border">
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Property</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Units</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Occupancy</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Vacancy Rate</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Turnover</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Avg Days Vacant</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-light-border dark:divide-dark-border">
                                        {filteredData.map(property => {
                                            const status = getOccupancyStatus(property.occupancyRate);
                                            return (
                                                <tr key={property.propertyId} className="hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                                                    <td className="py-4">
                                                        <span className="font-medium text-light-text dark:text-dark-text">{property.propertyName}</span>
                                                    </td>
                                                    <td className="py-4 text-light-text dark:text-dark-text">
                                                        {property.occupiedUnits}/{property.totalUnits}
                                                    </td>
                                                    <td className="py-4">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-24 h-2 bg-light-bg dark:bg-dark-bg rounded-full overflow-hidden">
                                                                <div className="h-full bg-primary rounded-full" style={{ width: `${property.occupancyRate}%` }}></div>
                                                            </div>
                                                            <span className={`text-sm font-medium ${status.color}`}>{property.occupancyRate}%</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 text-light-text dark:text-dark-text">{property.vacancyRate}%</td>
                                                    <td className="py-4 text-light-text dark:text-dark-text">{property.turnoverRate}%</td>
                                                    <td className="py-4 text-light-text dark:text-dark-text">{property.avgDaysVacant} days</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeView === 'trends' && (
                        <div className="space-y-6">
                            {filteredData.map(property => (
                                <div key={property.propertyId} className="space-y-4">
                                    <h4 className="text-lg font-bold text-light-text dark:text-dark-text">{property.propertyName}</h4>
                                    
                                    {/* Occupancy Trend Chart */}
                                    <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-6">
                                        <h5 className="text-sm font-medium text-light-text dark:text-dark-text mb-4">Monthly Occupancy Rate</h5>
                                        <div className="h-48 flex items-end justify-between gap-2">
                                            {property.monthlyTrend.map((data, index) => (
                                                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                                    <div className="w-full bg-primary/20 rounded-t-lg relative" style={{ height: `${(data.occupancyRate / 100) * 100}%` }}>
                                                        <div className="absolute bottom-0 w-full bg-primary rounded-t-lg" style={{ height: `${data.occupancyRate}%` }}></div>
                                                    </div>
                                                    <span className="text-xs text-light-subtle dark:text-dark-subtle">{data.month}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Move-in/Move-out Stats */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                            <h5 className="text-sm font-medium text-light-text dark:text-dark-text mb-3">Monthly Activity</h5>
                                            <div className="space-y-2">
                                                {property.monthlyTrend.map((data, index) => (
                                                    <div key={index} className="flex items-center justify-between text-sm">
                                                        <span className="text-light-subtle dark:text-dark-subtle">{data.month}</span>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-green-600 dark:text-green-400">+{data.newTenants}</span>
                                                            <span className="text-red-600 dark:text-red-400">-{data.moveOuts}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                            <h5 className="text-sm font-medium text-light-text dark:text-dark-text mb-3">Key Metrics</h5>
                                            <div className="space-y-3">
                                                <div>
                                                    <p className="text-xs text-light-subtle dark:text-dark-subtle">Avg Occupancy Duration</p>
                                                    <p className="text-lg font-bold text-light-text dark:text-dark-text">{property.avgOccupancyDuration} months</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-light-subtle dark:text-dark-subtle">Projected Revenue</p>
                                                    <p className="text-lg font-bold text-light-text dark:text-dark-text">{formatCurrency(property.projectedRevenue)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-light-subtle dark:text-dark-subtle">Actual Revenue</p>
                                                    <p className="text-lg font-bold text-light-text dark:text-dark-text">{formatCurrency(property.actualRevenue)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeView === 'units' && (
                        <div className="space-y-6">
                            {filteredData.map(property => (
                                <div key={property.propertyId} className="space-y-4">
                                    <h4 className="text-lg font-bold text-light-text dark:text-dark-text">{property.propertyName}</h4>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead>
                                                <tr className="border-b border-light-border dark:border-dark-border">
                                                    <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Unit</th>
                                                    <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Status</th>
                                                    <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Tenant</th>
                                                    <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Details</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-light-border dark:divide-dark-border">
                                                {property.unitStatus.map((unit, index) => {
                                                    const statusStyle = getUnitStatusStyle(unit.status);
                                                    const StatusIcon = statusStyle.icon;
                                                    return (
                                                        <tr key={index} className="hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                                                            <td className="py-4">
                                                                <span className="font-medium text-light-text dark:text-dark-text">{unit.unitNumber}</span>
                                                            </td>
                                                            <td className="py-4">
                                                                <span className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                                                                    <StatusIcon size={14} />
                                                                    {unit.status}
                                                                </span>
                                                            </td>
                                                            <td className="py-4 text-light-text dark:text-dark-text">
                                                                {unit.tenantName || '-'}
                                                            </td>
                                                            <td className="py-4 text-sm text-light-subtle dark:text-dark-subtle">
                                                                {unit.leaseEnd && `Lease ends: ${new Date(unit.leaseEnd).toLocaleDateString()}`}
                                                                {unit.daysVacant && `${unit.daysVacant} days vacant`}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};